// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this component to implement:
 * - Lazy loading feature: Images will be loadedwhen they are visible in viewports
 * - Responisve images using picture element
 */
import { getBynderUrl, TransformationsOptions } from "@eisinc/XDL/Utilities/bynder";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import qs from "qs";
import React from "react";
import HTMLEllipsis from "react-lines-ellipsis/lib/html";
import { connect } from "react-redux";
import styled from "styled-components";

export const mapStateToProps = (state: ApplicationState) => ({
    bynderBaseUrl: (getSettingsCollection(state).customSettings as any)?.bynderBaseUrl,
});

/**
 * if the image loads in under this number of milliseconds, it doesn't fade in.
 */
const fadeInThreshold = 100;

// <XNG-Changes> MIDPT-2
export interface LazyImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LazyImageProps>;
    /** Optional content to show while image is loading.
     * @themable */
    placeholder?: React.ReactNode;
    /** The height of the image.
     * @themable */
    height?: string;
    /** The width of the image.
     * @themable */
    width?: string;
    /** Props passed to the icon component that displays when the image fails to load.
     * @themable */
    errorIconProps?: IconPresentationProps;
    /** Props passed to the typography component that displays when the image fails to load.
     * @themable */
    errorTypographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    transformations?: TransformationsOptions;
    /** use This flag to disable image lazyloading */
    disableLazyLoad?: boolean;
}
// </XNG-Changes>

type State = {
    src?: string;
    loaded: boolean;
    error: boolean;
    startTime: number;
    imageShouldFade: boolean;
    showPlaceholder: boolean;
};
// <XNG-Changes> MIDPT-2
export const LazyImageStyle = styled.div<
    Pick<State, "error" | "imageShouldFade" | "loaded"> & { isShareEntity?: boolean }
>`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${({ error, theme }) => (error ? resolveColor("common.accent", theme) : null)};
    ${({ error }) => error && "padding: 5px;"}
    width: ${getProp("width")};
    height: ${getProp("height")};
    overflow: hidden;
    img {
        width: 100%;
        height: auto;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 1;" : "opacity: 0;")}
        ${({ error }) => error && "padding: 3px;"}
    }
    .LazyImage-Placeholder {
        width: 100%;
        height: 100%;
        position: absolute;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 0;" : "opacity: 1;")}
    }
    p {
        text-align: center;
        display: inline-block;
        padding: 3px;
        width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }
    ${injectCss}
`;
// </XNG-Changes>

export type LazyImageProps = MobiusStyledComponentProps<
    "div",
    LazyImagePresentationProps & {
        /** Props to be passed into the inner `img` element. */
        imgProps?: JSX.IntrinsicElements["img"];
        /** The URL to fetch the image from. If not provided, LazyImage renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Callback function that will be called when the image has finished loading. */
        onLoad?: () => void;
        /** Callback function that will be called if an error occurs when loading an image. */
        onError?: () => void;
        /** Allows to detect pdf generation */
        isShareEntity?: boolean;

        /** <xng-change> */
        /** Props to be passed into the inner `picture` element. */
        pictureProps?: JSX.IntrinsicElements["picture"];
        /** The URL to fetch the image from for small viewports */
        smallImageSrc?: string;
        /** The URL to fetch the image from for medium viewports */
        mediumImageSrc?: string;
        /** The URL to fetch the image from for large viewports */
        largeImageSrc?: string;
        /** Show elipses on alt */
        showElipses?: boolean;
        /** Number of elipses lines */
        showElipsesOn?: string;
        /** </xng-change> */
    }
>;
export type Props = LazyImageProps & ReturnType<typeof mapStateToProps>;

/**
 * Defers loading of an image until after its first render. If the asset takes over 100ms to load, the image is faded
 * into view.
 */
export class LazyImage extends React.Component<Props, State> {
    image?: HTMLImageElement;
    // this parameter is used to clear the timeout call on unmount
    fadeInTimeout?: NodeJS.Timeout;
    reloadTimeout?: NodeJS.Timeout;
    state: State = {
        loaded: false,
        error: false,
        startTime: 0,
        imageShouldFade: false,
        showPlaceholder: false,
    };

    width?: number | string;
    height?: number | string;

    imageRef: React.RefObject<HTMLImageElement | null>;

    constructor(props: Props) {
        super(props);
        this.imageRef = React.createRef<HTMLImageElement | null>();

        const queryString = props.src && /\?.*/.exec(props.src);

        if (queryString) {
            const obj: { width?: string; height?: string } = qs.parse(queryString[0], { ignoreQueryPrefix: true });

            this.width = obj.width;
            this.height = obj.height;
        } else {
            this.width = "";
            this.height = "";
        }
    }

    /** <xng-change> */
    onLoadHandler = () => {
        this.setState(
            currentState => ({
                // fade if image takes more than 100ms to load (i.e. not cached)
                imageShouldFade: Date.now() - currentState.startTime > fadeInThreshold,
                loaded: true,
                src: this.props.src,
            }),
            this.props.onLoad,
        );
    };

    onErrorHandler = () => {
        this.setState(
            {
                loaded: true,
                error: true,
            },
            this.props.onError,
        );
    };

    /** </xng-change> */
    reloadImage() {
        this.setState({ startTime: Date.now(), loaded: false });
        this.imageRef.current?.classList.add("lazyload");
        this.fadeInTimeout = setTimeout(() => {
            this.setState({ showPlaceholder: true });
        }, fadeInThreshold);
    }

    componentDidMount() {
        this.reloadImage();

        this.reloadTimeout = setTimeout(() => {
            this.onLoadHandler();
        }, 100);
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.src !== prevProps.src) {
            this.reloadImage();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.fadeInTimeout);
        clearTimeout(this.reloadTimeout);
    }

    /** <xng-change> */
    hasInvalidProps() {
        return !this.props.src;
    }

    checkIfBynderUrl(baseUrl?: string) {
        return baseUrl?.toLowerCase().includes("media.eis-inc.com");
    }

    getImageSrc(src?: string) {
        const isBynderUrl = this.checkIfBynderUrl(src);

        if (
            src &&
            (src.toLowerCase().indexOf("wwww.") > -1 ||
                (src.toLowerCase().indexOf("http") > -1 && src.toLowerCase().indexOf("res.") === -1) ||
                src.toLowerCase().indexOf("userfiles") > -1 ||
                src.toLowerCase().indexOf("images") > -1 ||
                src?.toLowerCase().indexOf("cloudinary") > -1)
        ) {
            return src;
        }

        if (src && isBynderUrl) {
            return getBynderUrl(src, this.props.bynderBaseUrl, {
                ...this.props.transformations,
                width: this.props.transformations?.width || this.width,
            });
        }
    }

    renderImage(src?: string) {
        let { src: propSrc } = this.props;
        const { imgProps, altText } = this.props;
        const { error, loaded, src: stateSrc } = this.state;
        propSrc = loaded && !error ? stateSrc : propSrc;

        return (
            <img
                ref={this.imageRef}
                alt={altText}
                className="lazyload"
                width={this.width}
                height={this.height}
                onLoad={this.onLoadHandler}
                onError={this.onErrorHandler}
                data-src={this.getImageSrc(propSrc)}
                {...(imgProps as
                    | React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
                    | undefined)}
            />
        );
    }

    /** </xng-change> */
    render() {
        const {
            mergeCss,
            src: propSrc,
            placeholder,
            imgProps,
            altText,
            showElipses,
            showElipsesOn,
            isShareEntity,
            disableLazyLoad,
            ...otherProps
        } = this.props;
        const { error, imageShouldFade, loaded, showPlaceholder, src: stateSrc } = this.state;
        const { applyStyledProp, spreadProps } = applyPropBuilder(this.props, { component: "lazyImage" });
        const resolvedMergeCss = mergeCss ?? this.props?.theme?.lazyImage?.defaultProps?.mergeCss;

        /** <xng-change> */
        if (this.hasInvalidProps()) {
            return null;
        }
        /** </xng-change> */

        if (disableLazyLoad || isShareEntity) {
            return (
                <LazyImageStyle
                    css={applyStyledProp("css", resolvedMergeCss)}
                    {...{ error, imageShouldFade, loaded: true, isShareEntity }}
                    {...otherProps}
                >
                    <img
                        alt={altText}
                        className="lazyloaded"
                        src={propSrc}
                        {...(imgProps as
                            | React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
                            | undefined)}
                    />
                </LazyImageStyle>
            );
        }

        return (
            <LazyImageStyle
                css={applyStyledProp("css", resolvedMergeCss)}
                {...{ error, imageShouldFade, loaded, isShareEntity }}
                {...otherProps}
            >
                {showPlaceholder && <span className="LazyImage-Placeholder">{placeholder}</span>}
                {loaded && error && <IconMemo {...spreadProps("errorIconProps")} title={altText} />}
                {!error && /** <xng-change> */ this.renderImage() /** </xng-change> */}
                {error && (
                    <Typography className="LazyImage-ErrorMessage" {...spreadProps("errorTypographyProps")} as="p">
                        {showElipses ? (
                            <HTMLEllipsis
                                unsafeHTML={altText}
                                maxLine={showElipsesOn}
                                ellipsisHTML="..."
                                basedOn="letters"
                            />
                        ) : (
                            <>{altText}</>
                        )}
                    </Typography>
                )}
            </LazyImageStyle>
        );
    }

    static defaultProps = {
        height: "100%",
        width: "auto",
    };
}

/** @component */
export default connect(mapStateToProps)(LazyImage);
